import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Copy, Globe, Link2, Lock, LogOut, MousePointerClick, Trash2, Upload, Users, Wand2 } from "lucide-react";
import AdminLogin from "@/components/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 8 * 1024 * 1024;

const inputCls =
  "w-full border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-white/40";
const labelCls = "mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500";

function Stat({ label, value, testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.03] p-5" data-testid={testId}>
      <p className="font-serif text-3xl font-light">{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
    </div>
  );
}

const RANGES = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All time"]];
const PLATFORM_LABEL = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", facebook: "Facebook", twitter: "X / Twitter",
  snapchat: "Snapchat", linkedin: "LinkedIn", reddit: "Reddit", pinterest: "Pinterest", whatsapp: "WhatsApp",
  telegram: "Telegram", discord: "Discord", email: "Email", google: "Google search", bing: "Bing", podcast: "Podcast",
  direct: "Direct / typed in", unknown: "Unknown",
};
const LINK_PLATFORMS = ["instagram", "tiktok", "youtube"];

function PlatformRows({ rows, testId }) {
  const max = Math.max(1, ...rows.map((p) => p.visitors));
  return (
    <div className="mt-3 space-y-2" data-testid={testId}>
      {rows.length === 0 && <p className="text-xs text-neutral-600">No traffic yet</p>}
      {rows.map((p) => (
        <div key={p.platform} className="text-xs" data-testid={`${testId}-${p.platform}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="truncate text-neutral-300">{PLATFORM_LABEL[p.platform] || p.platform}</span>
            <span className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-neutral-500">
              <span className="text-white">{p.visitors}</span> visitors · <span className="text-white">{p.reached_form}</span> saw form ·{" "}
              <span className={p.applied ? "text-green-400" : "text-white"}>{p.applied}</span> applied
            </span>
          </div>
          <div className="mt-1 h-1 bg-white/10">
            <div className="h-full bg-white/70" style={{ width: `${Math.round((p.visitors / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [cred, setCred] = useState(localStorage.getItem("vsl_admin_key") || "");
  const [state, setState] = useState("checking");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("30d");
  const [showRecent, setShowRecent] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("vsl_admin_key");
    if (!stored) {
      setState("out");
      return;
    }
    fetch(`${API}/builder/draft`, { headers: { "x-admin-key": stored } })
      .then((r) => {
        if (r.ok) {
          setCred(stored);
          setState("in");
        } else {
          localStorage.removeItem("vsl_admin_key");
          setState("out");
        }
      })
      .catch(() => setState("out"));
  }, []);

  useEffect(() => {
    if (state !== "in") return;
    fetch(`${API}/analytics/summary?range=${range}`, { headers: { "x-admin-key": cred } })
      .then((r) => r.json())
      .then(setAnalytics)
      .catch(() => {});
  }, [state, cred, range]);

  const logout = () => {
    localStorage.removeItem("vsl_admin_key");
    setState("out");
    toast.success("Signed out");
  };

  const changePassword = async () => {
    if (!pwCurrent || !pwNew) {
      toast.error("Fill in both password fields.");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setPwBusy(true);
    try {
      const r = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": cred },
        body: JSON.stringify({ current_password: pwCurrent, new_password: pwNew }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed");
      toast.success("Password updated. Use it on your next sign-in.");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPwBusy(false);
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose a video file first.");
      return;
    }
    setBusy(true);
    setProgress(0);
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    try {
      const uploadChunk = async (i) => {
        const res = await fetch(`${API}/vsl/upload/chunk?upload_id=${uploadId}&index=${i}`, {
          method: "POST",
          headers: { "x-admin-key": cred, "Content-Type": "application/octet-stream" },
          body: file.slice(i * CHUNK, (i + 1) * CHUNK),
        });
        if (!res.ok) throw new Error(`Chunk ${i + 1} failed (${res.status})`);
      };
      const uploadChunkWithRetry = async (i) => {
        for (let attempt = 0; attempt < 5; attempt++) {
          try {
            setStatus(`Uploading chunk ${i + 1} of ${total}…`);
            await uploadChunk(i);
            setProgress(Math.round(((i + 1) / total) * 100));
            return;
          } catch (e) {
            if (attempt === 4) throw e;
            setStatus(`Chunk ${i + 1} hiccup — retrying (${attempt + 1}/4)…`);
            await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
      };
      for (let i = 0; i < total; i++) await uploadChunkWithRetry(i);

      setStatus("Upload complete — preparing your video…");
      let finalized = false;
      for (let fin = 0; fin < 3 && !finalized; fin++) {
        const done = await fetch(
          `${API}/vsl/upload/complete?upload_id=${uploadId}&filename=${encodeURIComponent(file.name)}&total_chunks=${total}&size=${file.size}&chunk_size=${CHUNK}`,
          { method: "POST", headers: { "x-admin-key": cred } }
        );
        if (done.ok) {
          finalized = true;
          break;
        }
        const d = await done.json().catch(() => ({}));
        const msg = typeof d.detail === "string" ? d.detail : `Finalize failed (${done.status})`;
        const resumable = done.status === 400;
        if (!resumable) throw new Error(msg);
        if (fin === 2) throw new Error("Upload couldn't finish after several attempts — check your connection and try again.");
        setStatus("Server hiccup — resuming upload automatically…");
        const st = await fetch(`${API}/vsl/upload/status?upload_id=${uploadId}`, { headers: { "x-admin-key": cred } })
          .then((r) => r.json())
          .catch(() => ({ chunks: [] }));
        const have = new Set(st.chunks || []);
        for (let i = 0; i < total; i++) {
          if (!have.has(i)) await uploadChunkWithRetry(i);
        }
      }
      let unknowns = 0;
      const deadline = Date.now() + 40 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const jr = await fetch(`${API}/vsl/job/${uploadId}`, { headers: { "x-admin-key": cred } });
        const job = await jr.json().catch(() => ({ state: "unknown" }));
        if (job.state === "ready") break;
        if (job.state === "error") throw new Error(job.error || "Conversion failed");
        unknowns = job.state === "unknown" ? unknowns + 1 : 0;
        if (unknowns > 10)
          throw new Error("The server lost track of this upload — refresh and press Upload again; your progress is saved and it will resume.");
        if (Date.now() > deadline)
          throw new Error("This is taking unusually long — check your connection and try again.");
        const pct = typeof job.progress === "number" ? ` ${job.progress}%` : "";
        setStatus(
          job.state === "unknown"
            ? "Server is restarting — resuming automatically…"
            : job.state === "inspecting"
              ? "Checking your video format…"
              : job.state === "assembling"
                ? `Assembling your video…${pct}`
                : job.state === "converting"
                  ? `Converting to web format…${pct} — keep this tab open`
                  : job.state === "publishing"
                    ? `Publishing your video…${pct}`
                    : "Preparing your video…"
        );
      }
      setStatus("Done — your VSL is now live on the homepage.");
      toast.success("VSL uploaded and live.");
    } catch (e) {
      setStatus(`Error: ${e.message}`);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const [affName, setAffName] = useState("");
  const [affiliates, setAffiliates] = useState(null);
  const [affBusy, setAffBusy] = useState(false);
  const [expandedAff, setExpandedAff] = useState(null);

  const loadAffiliates = () => {
    fetch(`${API}/affiliates`, { headers: { "x-admin-key": cred } })
      .then((r) => r.json())
      .then(setAffiliates)
      .catch(() => {});
  };

  useEffect(() => {
    if (state === "in") loadAffiliates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, cred]);

  const createAffiliate = async () => {
    if (!affName.trim()) {
      toast.error("Give the link a name first.");
      return;
    }
    setAffBusy(true);
    try {
      const r = await fetch(`${API}/affiliates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-admin-key": cred },
        body: JSON.stringify({ name: affName.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed");
      toast.success(`Link created: /r/${data.slug}`);
      setAffName("");
      loadAffiliates();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAffBusy(false);
    }
  };

  const removeAffiliate = async (slug) => {
    if (!window.confirm(`Delete link "${slug}"? Past tracking data stays.`)) return;
    await fetch(`${API}/affiliates/${slug}`, { method: "DELETE", headers: { "x-admin-key": cred } });
    loadAffiliates();
  };

  const copyLink = (slug, platform) => {
    const url = `${window.location.origin}/r/${slug}${platform ? `?s=${platform}` : ""}`;
    navigator.clipboard.writeText(url);
    toast.success(platform ? `${PLATFORM_LABEL[platform] || platform} link copied` : "Link copied");
  };

  if (state === "checking") {
    return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] text-xs uppercase tracking-[0.3em] text-neutral-500">Loading…</div>;
  }

  if (state === "out") {
    return <AdminLogin title="Admin Dashboard" onSuccess={(c) => { setCred(c); setState("in"); }} />;
  }

  const deviceTotal = analytics ? Object.values(analytics.devices).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] px-6 py-10 font-sans text-white" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-500">Sergio Chavez</p>
            <h1 className="mt-2 font-serif text-3xl font-light">Admin Dashboard</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-white/50 hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <a
            href="/builder"
            data-testid="dashboard-builder-card"
            className="group border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/30"
          >
            <Wand2 className="h-5 w-5 text-neutral-400 transition-colors group-hover:text-white" />
            <p className="mt-4 font-serif text-xl font-light">Visual Builder</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-500">
              Edit the site visually — drag sections, change text, colors and spacing, then publish.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-400 group-hover:text-white">
              Open <ArrowUpRight className="h-3 w-3" />
            </span>
          </a>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dashboard-site-card"
            className="group border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/30"
          >
            <Globe className="h-5 w-5 text-neutral-400 transition-colors group-hover:text-white" />
            <p className="mt-4 font-serif text-xl font-light">View live site</p>
            <p className="mt-2 text-xs leading-relaxed text-neutral-500">
              See exactly what your visitors see right now.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-400 group-hover:text-white">
              Open <ArrowUpRight className="h-3 w-3" />
            </span>
          </a>
        </div>

        <div className="mt-4 border border-white/10 bg-black/40 p-8" data-testid="analytics-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Users className="h-5 w-5 text-neutral-400" />
              <p className="font-serif text-xl font-light">Website analytics</p>
            </div>
            <div className="flex gap-1" data-testid="analytics-range">
              {RANGES.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setRange(key)}
                  data-testid={`analytics-range-${key}`}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${range === key ? "bg-white text-black" : "border border-white/10 text-neutral-500 hover:text-white"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {!analytics ? (
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-neutral-600">Loading…</p>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Unique visitors" value={analytics.visitors} testId="stat-visitors" />
                <Stat label="Sessions" value={analytics.sessions} testId="stat-sessions" />
                <Stat label="Pageviews" value={analytics.pageviews} testId="stat-pageviews" />
                <Stat label="Clicks" value={analytics.clicks} testId="stat-clicks" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border border-white/10 bg-white/[0.03] px-5 py-4 text-[10px] uppercase tracking-[0.2em] text-neutral-500" data-testid="funnel-strip">
                <span><span className="text-sm text-white">{analytics.funnel.visitors}</span> visitors</span>
                <span className="text-neutral-700">→</span>
                <span><span className="text-sm text-white">{analytics.funnel.reached_form}</span> reached form</span>
                <span className="text-neutral-700">→</span>
                <span><span className="text-sm text-green-400">{analytics.funnel.applied}</span> applied</span>
                {analytics.funnel.visitors > 0 && (
                  <span className="text-neutral-600">
                    ({Math.round((analytics.funnel.applied / analytics.funnel.visitors) * 100)}% conversion)
                  </span>
                )}
              </div>

              <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Where visitors come from</p>
              <PlatformRows rows={analytics.platforms} testId="analytics-platforms" />

              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Devices</p>
                  <div className="mt-3 space-y-2" data-testid="analytics-devices">
                    {["desktop", "mobile", "tablet"].map((d) => (
                      <div key={d} className="flex items-center gap-3 text-xs">
                        <span className="w-16 capitalize text-neutral-400">{d}</span>
                        <div className="h-1.5 flex-1 bg-white/10">
                          <div
                            className="h-full bg-white/70"
                            style={{ width: deviceTotal ? `${Math.round(((analytics.devices[d] || 0) / deviceTotal) * 100)}%` : "0%" }}
                          />
                        </div>
                        <span className="w-8 text-right text-neutral-500">{analytics.devices[d] || 0}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Locations</p>
                  <div className="mt-3 space-y-1.5" data-testid="analytics-locations">
                    {analytics.locations.length === 0 && <p className="text-xs text-neutral-600">No data yet</p>}
                    {analytics.locations.map(([loc, count]) => (
                      <div key={loc} className="flex justify-between text-xs text-neutral-400">
                        <span>{loc}</span>
                        <span className="text-neutral-600">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Top clicked elements</p>
                  <div className="mt-3 space-y-1.5" data-testid="analytics-top-clicks">
                    {analytics.top_clicks.length === 0 && <p className="text-xs text-neutral-600">No clicks yet</p>}
                    {analytics.top_clicks.map((c) => (
                      <div key={c.element} className="flex items-center justify-between gap-2 text-xs text-neutral-400">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <MousePointerClick className="h-3 w-3 shrink-0 text-neutral-600" />
                          <span className="truncate">{c.element}</span>
                        </span>
                        <span className="shrink-0 text-neutral-600">{c.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Recent activity</p>
                <button
                  onClick={() => setShowRecent((v) => !v)}
                  data-testid="analytics-recent-toggle"
                  className="border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-white/40 hover:text-white"
                >
                  {showRecent ? "Hide" : `Show ${analytics.recent.length} events`}
                </button>
              </div>
              {showRecent && (
              <div className="mt-3 overflow-x-auto" data-testid="analytics-recent">
                <table className="w-full text-left text-xs text-neutral-400">
                  <thead>
                    <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-neutral-600">
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">Event</th>
                      <th className="py-2 pr-3">From</th>
                      <th className="py-2 pr-3">Device</th>
                      <th className="py-2 pr-3">Location</th>
                      <th className="py-2 pr-3">IP</th>
                      <th className="py-2">Element</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recent.map((e, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="whitespace-nowrap py-2 pr-3 text-neutral-500">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="py-2 pr-3">{e.type}</td>
                        <td className="py-2 pr-3 text-neutral-500">{PLATFORM_LABEL[e.platform] || e.platform || "—"}</td>
                        <td className="py-2 pr-3">{e.device}</td>
                        <td className="whitespace-nowrap py-2 pr-3">{e.city ? `${e.city}, ${e.country || ""}` : e.country || "—"}</td>
                        <td className="py-2 pr-3 text-neutral-600">{e.ip || "—"}</td>
                        <td className="max-w-[140px] truncate py-2 text-neutral-500">{e.element || "—"}</td>
                      </tr>
                    ))}
                    {analytics.recent.length === 0 && (
                      <tr><td colSpan={7} className="py-4 text-neutral-600">No activity yet — visit the site and come back.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </div>

        <div className="mt-4 border border-white/10 bg-black/40 p-8" data-testid="dashboard-upload-card">
          <div className="flex items-start gap-4">
            <Upload className="mt-1 h-5 w-5 text-neutral-400" />
            <div>
              <p className="font-serif text-xl font-light">Upload your VSL</p>
              <p className="mt-2 max-w-lg text-xs leading-relaxed text-neutral-500">
                Uploads go in chunks, so files over 200MB work fine. Any format is
                converted to web-ready video automatically. Once it finishes, the
                homepage hero plays this video.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className={labelCls} htmlFor="vsl_file">Video file (mp4 / mov / webm / hevc — anything)</label>
            <input
              id="vsl_file"
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-neutral-400 file:mr-4 file:border file:border-white/20 file:bg-transparent file:px-4 file:py-2.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-white hover:file:bg-white hover:file:text-black"
              data-testid="admin-file-input"
            />
            {file && (
              <p className="mt-2 text-xs text-neutral-500" data-testid="admin-file-info">
                {file.name} — {(file.size / (1024 * 1024)).toFixed(1)} MB
              </p>
            )}
          </div>

          {busy && (
            <div className="mt-6 h-1 w-full bg-white/10" data-testid="admin-progress-bar">
              <div className="h-full bg-white transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
          )}
          {status && (
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-neutral-400" data-testid="admin-upload-status">
              {status}
            </p>
          )}

          <button
            onClick={upload}
            disabled={busy}
            data-testid="admin-upload-btn"
            className="mt-6 flex items-center justify-center gap-3 bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-neutral-300 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {busy ? "Uploading…" : "Upload & go live"}
          </button>
        </div>

        <div className="mt-4 border border-white/10 bg-black/40 p-8" data-testid="affiliates-card">
          <div className="flex items-start gap-4">
            <Link2 className="mt-1 h-5 w-5 text-neutral-400" />
            <div>
              <p className="font-serif text-xl font-light">Affiliate links</p>
              <p className="mt-2 max-w-lg text-xs leading-relaxed text-neutral-500">
                Create a named link, share it, and see exactly who visited through it and who applied.
                To see applicant names in Typeform too, add a hidden field called <span className="text-neutral-300">ref</span> in your form settings.
              </p>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <input
              value={affName}
              onChange={(e) => setAffName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createAffiliate()}
              placeholder="Link name — e.g. Instagram bio, John podcast"
              className={inputCls}
              data-testid="affiliate-name-input"
            />
            <button
              onClick={createAffiliate}
              disabled={affBusy}
              data-testid="affiliate-create-btn"
              className="shrink-0 bg-white px-6 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-50"
            >
              Create
            </button>
          </div>

          <div className="mt-6 space-y-2" data-testid="affiliates-list">
            {affiliates === null && <p className="text-xs text-neutral-600">Loading…</p>}
            {affiliates?.length === 0 && <p className="text-xs text-neutral-600">No links yet — create your first one above.</p>}
            {affiliates?.map((a) => (
              <div key={a.slug} className="border border-white/10" data-testid={`affiliate-${a.slug}`}>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <button onClick={() => setExpandedAff(expandedAff === a.slug ? null : a.slug)} className="min-w-0 flex-1 text-left" data-testid={`affiliate-expand-${a.slug}`}>
                    <p className="truncate text-sm text-white">{a.name}</p>
                    <p className="truncate text-[11px] text-neutral-500">/r/{a.slug}</p>
                  </button>
                  <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-neutral-500" data-testid={`affiliate-funnel-${a.slug}`}>
                    <span><span className="text-white">{a.unique_visitors}</span> visitors</span>
                    <span className="text-neutral-700">→</span>
                    <span><span className="text-white">{a.form_views}</span> saw form</span>
                    <span className="text-neutral-700">→</span>
                    <span><span className={a.applications ? "text-green-400" : "text-white"}>{a.applications}</span> applied</span>
                    {a.unique_visitors > 0 && <span className="text-neutral-600">({Math.round((a.applications / a.unique_visitors) * 100)}%)</span>}
                  </div>
                  <button onClick={() => copyLink(a.slug)} className="p-1.5 text-neutral-400 transition-colors hover:text-white" title="Copy link" data-testid={`affiliate-copy-${a.slug}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => removeAffiliate(a.slug)} className="p-1.5 text-neutral-500 transition-colors hover:text-red-400" title="Delete link" data-testid={`affiliate-delete-${a.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {expandedAff === a.slug && (
                  <div className="border-t border-white/10 px-4 py-3" data-testid={`affiliate-detail-${a.slug}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Copy a link tagged for a platform</p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`affiliate-platform-links-${a.slug}`}>
                      {LINK_PLATFORMS.map((p) => (
                        <button
                          key={p}
                          onClick={() => copyLink(a.slug, p)}
                          data-testid={`affiliate-copy-${p}-${a.slug}`}
                          className="border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-white/40 hover:text-white"
                        >
                          {PLATFORM_LABEL[p]}
                        </button>
                      ))}
                    </div>
                    <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">By platform</p>
                    <PlatformRows rows={a.platforms || []} testId={`affiliate-platforms-${a.slug}`} />
                    <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Recent activity</p>
                    {a.recent.length === 0 && <p className="mt-2 text-xs text-neutral-600">No activity yet.</p>}
                    {a.recent.map((e, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 py-1 text-[11px] text-neutral-500">
                        <span className="text-neutral-400">{new Date(e.created_at).toLocaleString()}</span>
                        <span className={`uppercase tracking-wider ${e.type === "application" ? "text-green-400" : ""}`}>{e.type}</span>
                        <span>{PLATFORM_LABEL[e.platform] || e.platform || "—"}</span>
                        <span>{e.device}</span>
                        <span>{e.city ? `${e.city}, ${e.country || ""}` : e.country || "—"}</span>
                        <span className="text-neutral-600">{e.ip}</span>
                        {e.element && <span className="truncate text-neutral-600">{e.element}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 border border-white/10 bg-black/40 p-8" data-testid="password-card">
          <div className="flex items-start gap-4">
            <Lock className="mt-1 h-5 w-5 text-neutral-400" />
            <div>
              <p className="font-serif text-xl font-light">Change password</p>
              <p className="mt-2 max-w-lg text-xs leading-relaxed text-neutral-500">
                Minimum 10 characters. Your current session stays active.
              </p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls} htmlFor="pw_current">Current</label>
              <input id="pw_current" type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} className={inputCls} data-testid="pw-current" />
            </div>
            <div>
              <label className={labelCls} htmlFor="pw_new">New</label>
              <input id="pw_new" type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} className={inputCls} data-testid="pw-new" />
            </div>
            <div>
              <label className={labelCls} htmlFor="pw_confirm">Confirm new</label>
              <input id="pw_confirm" type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} className={inputCls} data-testid="pw-confirm" />
            </div>
          </div>
          <button
            onClick={changePassword}
            disabled={pwBusy}
            data-testid="pw-change-btn"
            className="mt-6 bg-white px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-50"
          >
            {pwBusy ? "Updating…" : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}
